/**
 * PropertyModal Component
 *
 * A shared property modal for displaying property details across
 * Admin, Agent, and Public apps.
 */
import { formatNeighborhoodName } from '@slowmaui/shared-types';
import { formatRelativeDate } from '../formatRelativeDate.js';
import { injectStyles } from './styles.js';
import { PhotoGallery } from './PhotoGallery.js';
import { PropertySections } from './PropertySections.js';
import { Disclaimers } from './Disclaimers.js';
import { toEmbeddableTourUrl, tourLabel } from '../tourUrl.js';
export class PropertyModal {
    options;
    property = null;
    listing = null;
    overlay = null;
    gallery = null;
    sections = null;
    disclaimers = null;
    constructor(options) {
        this.options = {
            showContactButton: true,
            showNeighborhoodLink: true,
            showFavoriteButton: true,
            showAlertButton: true,
            showAgentPickButton: false,
            contactButtonText: 'Contact Agent',
            ...options,
        };
        injectStyles();
        if (options.property) {
            this.property = options.property;
        }
        if (options.listing) {
            this.listing = options.listing;
        }
    }
    open(property) {
        this.property = property;
        this.listing = this.options.listing || null;
        this.render();
        this.show();
        // Load full MLS data if callback provided
        if (property.listingId && this.options.onLoadMlsData) {
            this.loadMlsData(property.listingId);
        }
        // Load sales history if callback provided and TMK is available
        if (property.tmkNormalized && this.options.onLoadSalesHistory) {
            this.loadSalesHistory(property.tmkNormalized);
        }
    }
    async loadMlsData(listingId) {
        if (!this.options.onLoadMlsData)
            return;
        try {
            const listing = await this.options.onLoadMlsData(listingId);
            if (listing) {
                this.updateWithMlsData(listing);
            }
        }
        catch (error) {
            console.error('Failed to load MLS data:', error);
        }
    }
    async loadSalesHistory(tmkId) {
        if (!this.options.onLoadSalesHistory)
            return;
        try {
            const sales = await this.options.onLoadSalesHistory(tmkId);
            if (sales && sales.length > 0) {
                this.updateWithSalesHistory(sales);
            }
        }
        catch (error) {
            console.error('Failed to load sales history:', error);
        }
    }
    updateWithSalesHistory(sales) {
        if (!this.property)
            return;
        // Merge sales into property data (avoid duplicates by date)
        const existingSales = this.property.sales || [];
        const existingDates = new Set(existingSales.map(s => s.saleDate));
        const newSales = sales.filter(s => !existingDates.has(s.saleDate));
        this.property.sales = [...existingSales, ...newSales]
            .sort((a, b) => new Date(b.saleDate).getTime() - new Date(a.saleDate).getTime());
        // Re-render sections to show updated sales history
        if (this.sections) {
            this.sections.updateProperty(this.property);
        }
    }
    updateWithMlsData(listing) {
        this.listing = listing;
        // Lift openHouse onto the property so the banner renderer (which reads
        // property.openHouse) can surface it once the MLS fetch returns.
        if (listing.openHouse && this.property) {
            this.property.openHouse = {
                date: listing.openHouse.date,
                start: listing.openHouse.start,
                end: listing.openHouse.end,
            };
        }
        // Update gallery with full photo set
        if (listing.photos && listing.photos.length > 0 && this.gallery) {
            this.gallery.updatePhotos(listing.photos);
        }
        // Update sections with full MLS data
        if (this.sections) {
            this.sections.updateListing(listing);
        }
        // Update MLS info bar
        this.updateMlsInfoBar();
        // Update Photos/Virtual Tour tabs - listing.virtualTourUrl often only
        // becomes known at this point (see updateMediaTabs's own comment)
        this.updateMediaTabs();
        // Re-render the open-house banner now that MLS data has arrived
        this.updateOpenHouse();
        // Update broker/agent info
        this.updateBrokerInfo();
        // Re-render stats (beds/baths/sqft/year) - buildPropertyStats() falls
        // back to `this.listing` for anything not already on the initial
        // `property` (e.g. a map-icon click, which opens with only status/price
        // from the lighter TmkStatusMarker payload - see
        // handleTmkStatusMarkerClick). Stats are only baked into the DOM once at
        // the initial render() though, so without this they stayed permanently
        // blank even after listing arrived with the real values.
        this.updatePropertyStats();
    }
    updatePropertyStats() {
        const container = this.overlay?.querySelector('#sm-property-stats-container');
        if (!container)
            return;
        container.innerHTML = this.buildPropertyStats();
    }
    render() {
        if (!this.property)
            return;
        // Remove existing modal if any (but don't null out property/listing)
        this.cleanup();
        // Create overlay
        this.overlay = document.createElement('div');
        this.overlay.className = 'sm-property-overlay';
        this.overlay.innerHTML = this.buildModalHtml();
        this.options.container.appendChild(this.overlay);
        // Initialize sub-components
        this.initializeSubComponents();
        this.attachEventListeners();
    }
    buildModalHtml() {
        const property = this.property;
        // Build title
        let title = property.address;
        if (property.unit) {
            title += ` #${property.unit}`;
        }
        // Status badge class
        const statusClass = property.status;
        const statusLabel = this.getStatusLabel(property.status);
        // Building name (if any)
        const buildingName = property.buildingName || '';
        return `
      <div class="sm-property-modal">
        <div class="sm-modal-header">
          <div class="sm-modal-header-left">
            <h2 class="sm-modal-title">${this.escapeHtml(title)}</h2>
            <div class="sm-modal-meta">
              <span class="sm-status-badge ${statusClass}">${statusLabel}</span>
              ${buildingName ? `<span class="sm-modal-building">${this.escapeHtml(buildingName)}</span>` : ''}
              <div class="sm-modal-meta-actions">
                ${this.buildActionButtons()}
              </div>
            </div>
            ${this.buildNeighborhoodLink(property)}
          </div>
          <div class="sm-modal-header-right">
            <button class="sm-modal-close" aria-label="Close">&times;</button>
          </div>
        </div>
        <div class="sm-modal-body">
          <!-- Photos / Virtual Tour - tabs only populated (see
               updateMediaTabs()) when this listing actually has a
               virtualTourUrl; otherwise this is just the gallery, same as
               before. -->
          <div id="sm-media-tabs" class="sm-media-tabs hidden"></div>
          <div id="sm-gallery-container"></div>
          <div id="sm-virtual-tour-container" class="sm-virtual-tour-container hidden"></div>

          <!-- Price Display -->
          ${this.buildPriceDisplay()}

          <!-- Property Stats -->
          <div id="sm-property-stats-container">${this.buildPropertyStats()}</div>

          <!-- View Tags -->
          ${this.buildViewTags()}

          <!-- Open House Banner -->
          <div id="sm-open-house-container"></div>

          <!-- MLS Info Bar -->
          <div id="sm-mls-info-bar"></div>

          <!-- Contact Button -->
          ${this.buildContactButton()}

          <!-- Collapsible Sections -->
          <div id="sm-sections-container"></div>

          <!-- Broker Attribution -->
          ${this.buildBrokerInfo()}

          <!-- Disclaimers -->
          <div id="sm-disclaimers-container"></div>
        </div>
      </div>
    `;
    }
    buildActionButtons() {
        const property = this.property;
        const tmk = property.tmkNormalized || property.listingId || '';
        const buttons = [];
        // Favorite button (heart) - always show if enabled
        if (this.options.showFavoriteButton) {
            const isFavorite = tmk ? (this.options.isFavorite?.(tmk) ?? false) : false;
            buttons.push(`
        <button class="sm-modal-action-btn sm-modal-favorite${isFavorite ? ' active' : ''}" aria-label="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}" title="${isFavorite ? 'Remove from favorites' : 'Add to favorites'}" data-tmk="${tmk}">
          <svg viewBox="0 0 24 24" fill="${isFavorite ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>
      `);
        }
        // Alert button (bell) - always show if enabled. Labeled with visible
        // text (not just a hover title, which mobile users never see) so the
        // value prop reads at a glance instead of requiring a guess at what the
        // icon means.
        if (this.options.showAlertButton) {
            const isAlerted = tmk ? (this.options.isAlerted?.(tmk) ?? false) : false;
            buttons.push(`
        <button class="sm-modal-action-btn sm-modal-alert sm-modal-alert-labeled${isAlerted ? ' active' : ''}" aria-label="${isAlerted ? 'Remove alert' : 'Set alert'}" title="${isAlerted ? 'Remove alert' : 'Get notified of price changes and sale updates'}" data-tmk="${tmk}">
          <svg viewBox="0 0 24 24" fill="${isAlerted ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          <span class="sm-modal-alert-label">${isAlerted ? 'Notified' : 'Notify me'}</span>
        </button>
      `);
        }
        // Share button
        buttons.push(`
      <button class="sm-modal-action-btn sm-modal-share" aria-label="Share" title="Share">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
      </button>
    `);
        // Agent Pick (Compile mode) - a shopping-cart icon (per Todd
        // 2026-10-26, was a plain checkbox rect) so it reads as "add this to
        // my Cart" rather than an unexplained generic toggle - same fill-on-
        // active treatment as Favorite/Alert above, no checkmark overlay
        // needed. Prefer the resolved MLS id (this.listing.id) once it's
        // loaded over the initial property.id, which is only a real
        // mls_listings.id for a Listings-Drawer/regular-map-click open - a
        // neighborhood-mode TmkStatusMarker click opens with property.id set
        // to the TMK instead (see handleTmkStatusMarkerClick), and
        // agent_cart_items.item_id must be the former.
        if (this.options.showAgentPickButton) {
            const itemId = this.listing?.id || property.id;
            const itemType = this.getAgentPickItemType();
            const isPicked = itemId ? (this.options.isAgentPicked?.(itemId, itemType) ?? false) : false;
            buttons.push(`
        <button class="sm-modal-action-btn sm-modal-agent-pick${isPicked ? ' active' : ''}" aria-label="${isPicked ? 'Remove from cart' : 'Add to cart'}" title="${isPicked ? 'Remove from cart' : 'Add to cart'}" data-item-id="${itemId}">
          <svg viewBox="0 0 24 24" fill="${isPicked ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
        </button>
      `);
        }
        return buttons.join('');
    }
    buildNeighborhoodLink(property) {
        if (!property.neighborhoodName)
            return '';
        const displayName = formatNeighborhoodName(property.neighborhoodName);
        if (this.options.showNeighborhoodLink && property.neighborhoodId && this.options.onNeighborhoodClick) {
            return `
        <button class="sm-modal-neighborhood-link" data-neighborhood-id="${property.neighborhoodId}">
          ${this.escapeHtml(displayName)}
        </button>
      `;
        }
        return `<span class="sm-modal-neighborhood">${this.escapeHtml(displayName)}</span>`;
    }
    buildPriceDisplay() {
        const property = this.property;
        const listing = this.listing;
        const price = property.status === 'sold'
            ? (property.closePrice || property.listPrice)
            : (property.listPrice || listing?.listPrice);
        if (!price)
            return '';
        const originalPrice = property.originalListPrice || listing?.originalListPrice;
        let priceChangeHtml = '';
        if (originalPrice && originalPrice !== price) {
            const change = price - originalPrice;
            const changePercent = ((change / originalPrice) * 100).toFixed(1);
            const isIncrease = change > 0;
            priceChangeHtml = `
        <span class="sm-price-change ${isIncrease ? 'increase' : 'drop'}">
          ${isIncrease ? '+' : ''}${changePercent}%
        </span>
      `;
        }
        // Recent Price Change attribute detail (distinct from the cumulative
        // %-since-listed above) - the specific most-recent change and when it
        // happened, matching the CNTL attribute's 7-day window.
        let recentChangeHtml = '';
        if (property.hasPriceChange && typeof property.previousPrice === 'number' && property.priceChangeDate) {
            const isIncrease = price > property.previousPrice;
            const arrow = isIncrease ? '&uarr;' : '&darr;';
            recentChangeHtml = `
        <div class="sm-price-recent-change ${isIncrease ? 'increase' : 'drop'}">
          ${arrow} ${this.formatPrice(property.previousPrice)} &rarr; ${this.formatPrice(price)} &middot; ${formatRelativeDate(property.priceChangeDate)}
        </div>
      `;
        }
        return `
      <div class="sm-price-display">
        <span class="sm-price-main">${this.formatPrice(price)}</span>
        ${priceChangeHtml}
      </div>
      ${recentChangeHtml}
    `;
    }
    buildPropertyStats() {
        const property = this.property;
        const listing = this.listing;
        const bedrooms = property.bedrooms ?? listing?.bedrooms ?? listing?.publicRemarks?.match(/(\d+)\s*bed/i)?.[1];
        const bathrooms = property.bathrooms ?? listing?.bathrooms;
        const sqft = property.sqft || listing?.livingArea;
        const yearBuilt = property.yearBuilt || listing?.yearBuilt;
        const stats = [];
        if (bedrooms !== undefined && bedrooms !== null) {
            stats.push({ value: String(bedrooms), label: 'Beds' });
        }
        if (bathrooms !== undefined && bathrooms !== null) {
            stats.push({ value: String(bathrooms), label: 'Baths' });
        }
        if (sqft) {
            stats.push({ value: sqft.toLocaleString(), label: 'Sq Ft' });
        }
        if (yearBuilt) {
            stats.push({ value: String(yearBuilt), label: 'Year' });
        }
        if (stats.length === 0)
            return '';
        return `
      <div class="sm-property-stats">
        ${stats.map(stat => `
          <div class="sm-stat-item">
            <div class="sm-stat-value">${stat.value}</div>
            <div class="sm-stat-label">${stat.label}</div>
          </div>
        `).join('')}
      </div>
    `;
    }
    buildViewTags() {
        const property = this.property;
        const listing = this.listing;
        const tags = [];
        // Ocean view
        if (property.hasOceanView || listing?.view?.some(v => v.toLowerCase().includes('ocean'))) {
            tags.push('<span class="sm-view-tag ocean">Ocean View</span>');
        }
        // Pool
        if (property.hasPool || (listing?.poolFeatures && listing.poolFeatures.length > 0)) {
            tags.push('<span class="sm-view-tag pool">Pool</span>');
        }
        // Spa
        if (property.hasSpa || (listing?.spaFeatures && listing.spaFeatures.length > 0)) {
            tags.push('<span class="sm-view-tag pool">Spa</span>');
        }
        // Waterfront
        if (property.isWaterfront || (listing?.waterfrontFeatures && listing.waterfrontFeatures.length > 0)) {
            tags.push('<span class="sm-view-tag waterfront">Waterfront</span>');
        }
        if (tags.length === 0)
            return '';
        return `<div class="sm-view-tags">${tags.join('')}</div>`;
    }
    buildContactButton() {
        if (!this.options.showContactButton)
            return '';
        const buttonText = this.options.contactButtonText || 'Contact Agent';
        return `
      <button class="sm-contact-btn">${this.escapeHtml(buttonText)}</button>
    `;
    }
    buildBrokerInfo() {
        // Just return a placeholder container - will be populated by updateBrokerInfo
        return '<div id="sm-broker-info-container"></div>';
    }
    updateBrokerInfo() {
        const container = this.overlay?.querySelector('#sm-broker-info-container');
        if (!container)
            return;
        const listing = this.listing;
        const property = this.property;
        // Get agent info from listing or property
        const agent = listing?.listAgent || property?.listAgent;
        if (!agent || (!agent.name && !agent.officeName)) {
            container.innerHTML = '';
            return;
        }
        let html = '<div class="sm-listing-agent-section">';
        html += '<div class="sm-listing-agent-header">Listing Agent</div>';
        html += '<div class="sm-listing-agent-card">';
        // Agent name
        if (agent.name) {
            html += `<div class="sm-agent-name">${this.escapeHtml(agent.name)}</div>`;
        }
        // Office name
        if (agent.officeName) {
            html += `<div class="sm-agent-office">${this.escapeHtml(agent.officeName)}</div>`;
        }
        // Contact info
        const hasContact = agent.phone || agent.email;
        if (hasContact) {
            html += '<div class="sm-agent-contact">';
            if (agent.phone) {
                html += `
          <a href="tel:${this.escapeHtml(agent.phone)}" class="sm-agent-contact-item">
            <span class="sm-contact-icon">📞</span>
            <span>${this.escapeHtml(this.formatPhone(agent.phone))}</span>
          </a>
        `;
            }
            if (agent.email) {
                html += `
          <a href="mailto:${this.escapeHtml(agent.email)}" class="sm-agent-contact-item">
            <span class="sm-contact-icon">✉️</span>
            <span>${this.escapeHtml(agent.email)}</span>
          </a>
        `;
            }
            html += '</div>';
        }
        html += '</div></div>';
        container.innerHTML = html;
    }
    formatPhone(phone) {
        // Clean the phone number
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        if (digits.length === 11 && digits[0] === '1') {
            return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        return phone;
    }
    updateOpenHouse() {
        const container = this.overlay?.querySelector('#sm-open-house-container');
        if (!container)
            return;
        const property = this.property;
        const openHouse = property?.openHouse;
        if (!openHouse || !openHouse.date) {
            container.innerHTML = '';
            return;
        }
        // Format the date nicely
        try {
            const dateObj = new Date(openHouse.date + 'T12:00:00');
            const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
            const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            let timeRange = '';
            if (openHouse.start && openHouse.end) {
                timeRange = `${openHouse.start} - ${openHouse.end}`;
            }
            else if (openHouse.start) {
                timeRange = openHouse.start;
            }
            container.innerHTML = `
        <div class="sm-open-house-banner">
          <div class="sm-open-house-icon">🏠</div>
          <div class="sm-open-house-details">
            <div class="sm-open-house-label">Open House</div>
            <div class="sm-open-house-date">${dayOfWeek}, ${monthDay}</div>
            ${timeRange ? `<div class="sm-open-house-time">${timeRange}</div>` : ''}
          </div>
        </div>
      `;
        }
        catch {
            container.innerHTML = '';
        }
    }
    updateMlsInfoBar() {
        const container = this.overlay?.querySelector('#sm-mls-info-bar');
        if (!container)
            return;
        const property = this.property;
        const listing = this.listing;
        if (!property?.listingId && !listing?.listingId) {
            container.innerHTML = '';
            return;
        }
        const mlsId = listing?.listingId || property?.listingId;
        const dom = listing?.daysOnMarket || property?.daysOnMarket;
        const virtualTourUrl = listing?.virtualTourUrl || property?.virtualTourUrl;
        let html = `
      <div class="sm-mls-info-bar">
        <div class="sm-mls-info-left">
          <span class="sm-mls-id">MLS# ${mlsId}</span>
          ${dom !== undefined ? `<span class="sm-mls-dom">${dom} days on market</span>` : ''}
        </div>
    `;
        if (virtualTourUrl) {
            html += `
        <a href="${this.escapeHtml(virtualTourUrl)}" target="_blank" rel="noopener noreferrer" class="sm-virtual-tour-link">
          ${tourLabel(virtualTourUrl)} &#8599;
        </a>
      `;
        }
        html += '</div>';
        container.innerHTML = html;
    }
    initializeSubComponents() {
        if (!this.overlay || !this.property)
            return;
        // Photo Gallery
        const galleryContainer = this.overlay.querySelector('#sm-gallery-container');
        if (galleryContainer) {
            const photos = this.listing?.photos || (this.property.primaryPhotoUrl ? [this.property.primaryPhotoUrl] : this.property.photos || []);
            this.gallery = new PhotoGallery({
                container: galleryContainer,
                photos,
            });
            this.gallery.render();
        }
        // Property Sections
        const sectionsContainer = this.overlay.querySelector('#sm-sections-container');
        if (sectionsContainer) {
            this.sections = new PropertySections({
                container: sectionsContainer,
                property: this.property,
                listing: this.listing || undefined,
            });
            this.sections.render();
        }
        // Disclaimers
        const disclaimersContainer = this.overlay.querySelector('#sm-disclaimers-container');
        if (disclaimersContainer) {
            this.disclaimers = new Disclaimers({
                container: disclaimersContainer,
                showIdx: true,
                showNeighborhood: false,
            });
            this.disclaimers.render();
        }
        // Initial open house banner
        this.updateOpenHouse();
        // Initial MLS info bar
        this.updateMlsInfoBar();
        // Initial Photos/Virtual Tour tabs
        this.updateMediaTabs();
        // Initial broker info (may update again when MLS data loads)
        this.updateBrokerInfo();
    }
    attachEventListeners() {
        if (!this.overlay)
            return;
        // Close button
        const closeBtn = this.overlay.querySelector('.sm-modal-close');
        closeBtn?.addEventListener('click', () => this.close());
        // Backdrop click
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.close();
            }
        });
        // Escape key
        document.addEventListener('keydown', this.handleKeydown);
        // Neighborhood link
        const neighborhoodBtn = this.overlay.querySelector('.sm-modal-neighborhood-link');
        neighborhoodBtn?.addEventListener('click', () => {
            const neighborhoodId = neighborhoodBtn.getAttribute('data-neighborhood-id');
            if (neighborhoodId && this.options.onNeighborhoodClick) {
                this.options.onNeighborhoodClick(neighborhoodId);
            }
        });
        // Contact button
        const contactBtn = this.overlay.querySelector('.sm-contact-btn');
        contactBtn?.addEventListener('click', () => {
            if (this.property && this.options.onContactClick) {
                this.options.onContactClick(this.property);
            }
        });
        // Share button
        const shareBtn = this.overlay.querySelector('.sm-modal-share');
        shareBtn?.addEventListener('click', () => this.handleShare());
        // Favorite button
        const favoriteBtn = this.overlay.querySelector('.sm-modal-favorite');
        favoriteBtn?.addEventListener('click', () => this.handleFavoriteToggle());
        // Alert button
        const alertBtn = this.overlay.querySelector('.sm-modal-alert');
        alertBtn?.addEventListener('click', () => this.handleAlertToggle());
        // Agent Pick button
        const agentPickBtn = this.overlay.querySelector('.sm-modal-agent-pick');
        agentPickBtn?.addEventListener('click', () => this.handleAgentPickToggle());
    }
    handleFavoriteToggle() {
        const tmk = this.property?.tmkNormalized;
        if (!tmk || !this.options.onFavoriteToggle)
            return;
        const btn = this.overlay?.querySelector('.sm-modal-favorite');
        if (!btn)
            return;
        const isCurrentlyFavorite = btn.classList.contains('active');
        const newState = !isCurrentlyFavorite;
        // Update UI immediately
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove from favorites' : 'Add to favorites');
        btn.setAttribute('title', newState ? 'Remove from favorites' : 'Add to favorites');
        // Update SVG fill
        const svg = btn.querySelector('svg');
        if (svg) {
            svg.setAttribute('fill', newState ? 'currentColor' : 'none');
        }
        // Notify callback
        this.options.onFavoriteToggle(tmk, newState);
        // Show toast
        this.showShareToast(newState ? 'Added to favorites' : 'Removed from favorites');
    }
    handleAlertToggle() {
        const tmk = this.property?.tmkNormalized;
        if (!tmk)
            return;
        // Alerts require login - checked before any visual change, not after.
        // No optimistic flip here; onAlertAuthRequired (main.ts) opens the auth
        // modal, and setAlertState() below is what actually updates this button
        // once sign-in completes and the alert is created for real.
        if (this.options.isLoggedIn && !this.options.isLoggedIn()) {
            this.options.onAlertAuthRequired?.(tmk);
            return;
        }
        if (!this.options.onAlertToggle)
            return;
        const btn = this.overlay?.querySelector('.sm-modal-alert');
        if (!btn)
            return;
        const isCurrentlyAlerted = btn.classList.contains('active');
        const newState = !isCurrentlyAlerted;
        this.applyAlertVisualState(btn, newState);
        // Notify callback
        this.options.onAlertToggle(tmk, newState);
        // Show toast
        this.showShareToast(newState ? 'Alert set for this property' : 'Alert removed');
    }
    // A property with ANY mls_listings history (even a resolved listing, or
    // just a listingId hint that hasn't resolved yet - cancelled/withdrawn/
    // expired rows aren't deleted, see workers/mls-sync) is a 'listing'
    // item keyed by that id. Only a parcel that's never been in MLS at all
    // (a bare TmkStatusMarker open with no listingId, see
    // handleTmkStatusMarkerClick in apps/agent/src/pages/AgentMap.ts) is
    // 'tmk', keyed by its TMK instead - matches migration 069's item_type.
    getAgentPickItemType() {
        return (this.listing?.id || this.property?.listingId) ? 'listing' : 'tmk';
    }
    // No toast here (unlike Favorite/Alert above) - the AgentDrawer already
    // toasts "Added/Removed <label>" once its own add/remove call resolves,
    // so toasting here too would double up.
    handleAgentPickToggle() {
        const itemId = this.listing?.id || this.property?.id;
        if (!itemId || !this.options.onAgentPickToggle)
            return;
        const itemType = this.getAgentPickItemType();
        const btn = this.overlay?.querySelector('.sm-modal-agent-pick');
        if (!btn)
            return;
        const isCurrentlyPicked = btn.classList.contains('active');
        const newState = !isCurrentlyPicked;
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove from Agent Picks' : 'Add to Agent Picks');
        btn.setAttribute('title', newState ? 'Remove from Agent Picks' : 'Add to Agent Picks');
        const svg = btn.querySelector('svg');
        const existingCheck = svg?.querySelector('polyline');
        if (newState && svg && !existingCheck) {
            svg.insertAdjacentHTML('beforeend', '<polyline points="7 12.5 10.5 16 17 8.5"></polyline>');
        }
        else if (!newState && existingCheck) {
            existingCheck.remove();
        }
        const label = this.property ? this.property.address + (this.property.unit ? ` #${this.property.unit}` : '') : 'listing';
        this.options.onAgentPickToggle(itemId, newState, label, itemType);
    }
    applyAlertVisualState(btn, newState) {
        btn.classList.toggle('active', newState);
        btn.setAttribute('aria-label', newState ? 'Remove alert' : 'Set alert');
        btn.setAttribute('title', newState ? 'Remove alert' : 'Get notified of price changes and sale updates');
        const svg = btn.querySelector('svg');
        if (svg) {
            svg.setAttribute('fill', newState ? 'currentColor' : 'none');
        }
        const label = btn.querySelector('.sm-modal-alert-label');
        if (label) {
            label.textContent = newState ? 'Notified' : 'Notify me';
        }
    }
    /**
     * Updates the alert button's visual state without a click - used after a
     * pending sign-in completes and the alert was actually created for the
     * tmk this modal is currently showing. No-ops if the modal has since
     * moved on to a different property.
     */
    setAlertState(tmk, isAlerted) {
        if (this.property?.tmkNormalized !== tmk)
            return;
        const btn = this.overlay?.querySelector('.sm-modal-alert');
        if (!btn)
            return;
        this.applyAlertVisualState(btn, isAlerted);
    }
    /**
     * Updates the Contact button's label (e.g. "Contact Agent" -> "Contact
     * Todd") after construction - this modal is a long-lived singleton
     * (main.ts constructs it once at startup), but the site's agent name is
     * resolved async from a subdomain lookup, so the real label is rarely
     * known yet at construction time. Takes effect the next time the modal
     * renders (buildContactButton() reads this.options.contactButtonText
     * fresh on every call); no-op if it's already open, by design, same as
     * any other options change here.
     */
    setContactButtonText(text) {
        this.options.contactButtonText = text;
    }
    async handleShare() {
        if (!this.property)
            return;
        const title = this.property.address || 'Property';
        const url = window.location.href;
        // Build share text
        let text = title;
        if (this.property.listPrice) {
            text += ` - $${this.property.listPrice.toLocaleString()}`;
        }
        // Try native share API first (mobile)
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url });
                return;
            }
            catch (err) {
                // User cancelled or share failed, fall back to clipboard
                if (err.name === 'AbortError')
                    return;
            }
        }
        // Fall back to clipboard
        try {
            await navigator.clipboard.writeText(url);
            this.showShareToast('Link copied to clipboard');
        }
        catch {
            // Final fallback: show URL in prompt
            prompt('Copy this link:', url);
        }
    }
    showShareToast(message) {
        // Create toast element
        const toast = document.createElement('div');
        toast.className = 'sm-share-toast';
        toast.textContent = message;
        document.body.appendChild(toast);
        // Trigger animation
        requestAnimationFrame(() => {
            toast.classList.add('show');
        });
        // Remove after delay
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
        }, 2000);
    }
    handleKeydown = (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('active')) {
            this.close();
        }
    };
    show() {
        if (this.overlay) {
            // Trigger reflow for animation
            void this.overlay.offsetHeight;
            this.overlay.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    }
    close() {
        // Only fire onClose when there's actually an open overlay to close. The
        // unconditional call was nuking the URL via callers' history.pushState
        // even when nothing was open (e.g. on initial page load when the router's
        // home-route handler proactively calls close()).
        if (!this.overlay)
            return;
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
        // Snapshot the instance being closed, then immediately detach `this`'s
        // fields from it - see GeographyModal.close()'s comment for why: without
        // this, a rapid reopen within the 300ms fade would have the deferred
        // cleanup() below tear down the NEW instance (whatever `this` points to
        // when it fires) while this closed instance's overlay/gallery leak
        // forever, un-removed from the DOM.
        const closed = this.snapshotResources();
        this.resetResourceFields();
        // Wait for animation before removing
        setTimeout(() => {
            this.cleanup(closed);
        }, 300);
        this.options.onClose?.();
    }
    snapshotResources() {
        return {
            overlay: this.overlay,
            gallery: this.gallery,
            sections: this.sections,
            disclaimers: this.disclaimers,
        };
    }
    resetResourceFields() {
        this.overlay = null;
        this.gallery = null;
        this.sections = null;
        this.disclaimers = null;
    }
    /** Tears down a specific snapshot of resources - never `this.*` directly,
     * so it's safe to run after a rapid reopen has already moved `this`'s
     * fields on to a newer instance (see close()). */
    cleanup(resources = this.snapshotResources()) {
        resources.gallery?.destroy();
        resources.sections?.destroy();
        resources.disclaimers?.destroy();
        resources.overlay?.remove();
        if (resources.overlay === this.overlay) {
            this.resetResourceFields();
        }
    }
    destroy() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.cleanup();
        this.property = null;
        this.listing = null;
    }
    getStatusLabel(status) {
        const labels = {
            'for-sale': 'For Sale',
            'pending': 'Pending',
            'sold': 'Sold',
            'off-market': 'Off Market',
            'rental': 'Rental',
        };
        return labels[status] || status;
    }
    formatPrice(amount) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
    /**
     * Builds the Photos/<Video|Virtual Tour> tab bar once a virtualTourUrl
     * is known (listing?.virtualTourUrl arrives later than
     * property?.virtualTourUrl for a map-pin-click open - see
     * updateMlsInfoBar's identical fallback - so this runs both at initial
     * render and again once the full MLS fetch resolves). The second tab's
     * label comes from tourLabel() - "Video" for a recognized video host,
     * "Virtual Tour" for a real 3D walkthrough or anything unrecognized.
     * No-ops (tabs stay hidden, gallery fills the space exactly like
     * before this feature existed) when there's no tour at all - most
     * listings.
     */
    updateMediaTabs() {
        const tabsContainer = this.overlay?.querySelector('#sm-media-tabs');
        const tourContainer = this.overlay?.querySelector('#sm-virtual-tour-container');
        const galleryContainer = this.overlay?.querySelector('#sm-gallery-container');
        if (!tabsContainer || !tourContainer || !galleryContainer)
            return;
        const virtualTourUrl = this.listing?.virtualTourUrl || this.property?.virtualTourUrl;
        if (!virtualTourUrl) {
            tabsContainer.classList.add('hidden');
            tabsContainer.innerHTML = '';
            return;
        }
        const label = tourLabel(virtualTourUrl);
        const embedUrl = toEmbeddableTourUrl(virtualTourUrl);
        tourContainer.innerHTML = embedUrl
            ? `<iframe class="sm-virtual-tour-iframe" src="${this.escapeHtml(embedUrl)}" title="${label}" allow="autoplay; encrypted-media; fullscreen" allowfullscreen></iframe>`
            : `
        <div class="sm-virtual-tour-fallback">
          <p>This ${label.toLowerCase()} opens on an external site.</p>
          <a href="${this.escapeHtml(virtualTourUrl)}" target="_blank" rel="noopener noreferrer" class="sm-contact-btn">Open ${label} &#8599;</a>
        </div>
      `;
        tabsContainer.classList.remove('hidden');
        tabsContainer.innerHTML = `
      <button type="button" class="sm-media-tab active" data-tab="photos">Photos</button>
      <button type="button" class="sm-media-tab" data-tab="tour">${label}</button>
    `;
        const photosTab = tabsContainer.querySelector('[data-tab="photos"]');
        const tourTab = tabsContainer.querySelector('[data-tab="tour"]');
        photosTab.addEventListener('click', () => {
            photosTab.classList.add('active');
            tourTab.classList.remove('active');
            galleryContainer.classList.remove('hidden');
            tourContainer.classList.add('hidden');
        });
        tourTab.addEventListener('click', () => {
            tourTab.classList.add('active');
            photosTab.classList.remove('active');
            galleryContainer.classList.add('hidden');
            tourContainer.classList.remove('hidden');
        });
    }
}
//# sourceMappingURL=PropertyModal.js.map